/* ──────────────────────────────────────────────────────────────
   shell/profileMenu.tsx · reusable account menu
   Trigger (pill in navbar / chip in sidebar). The card (avatar, name,
   email, Log out) shows on HOVER on web, and on tap on native.
   User data comes from /v1/me via useSession() and updates dynamically.
   ────────────────────────────────────────────────────────────── */
import React, { useState } from 'react';
import { View, Text, TouchableOpacity, ViewStyle, TextStyle } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useSession } from '../context/sessionContext';
import { useResponsive, isWeb } from '../lib/responsive';
import { Icon } from '../components/ui';

function initialsOf(name: string) {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? '').join('').toUpperCase() || 'A';
}

function GradientAvatar({ name, size }: { name: string; size: number }) {
  const { theme } = useTheme();
  const { colors } = theme;
  const ring: ViewStyle = {
    width: size, height: size, borderRadius: size / 2, alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#F97316',
    ...(isWeb ? ({ backgroundImage: 'linear-gradient(160deg, #FB923C 0%, #F43F5E 100%)' } as any) : {}),
  };
  return (
    <View style={ring}>
      <Text style={{ color: colors.white, fontWeight: '800', fontSize: size * 0.4, fontFamily: theme.font.sans, includeFontPadding: false }}>
        {initialsOf(name)}
      </Text>
    </View>
  );
}

export interface ProfileMenuProps {
  variant: 'navbar' | 'sidebar';
  onLogout?: () => void;
  /** Sidebar collapsed → show only the avatar. */
  compact?: boolean;
}

export default function ProfileMenu({ variant, onLogout, compact = false }: ProfileMenuProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  const { user, fullName } = useSession();
  const { width } = useResponsive();

  // Content-sized card: at least 260, never wider than its content or the
  // viewport. This keeps it anchored to the avatar instead of stretching
  // across the navbar on small screens.
  const cardMaxWidth = Math.min(320, Math.max(240, width - 24));

  const name = (fullName || '').trim() || user?.email || 'Account';
  const email = user?.email || '';
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);

  // The card is visible while hovering OR once the trigger is clicked
  // ("pinned"). Pinning keeps it open even if the cursor wanders off, so the
  // Log out button is always easy to reach. Outside click / logout closes it.
  const open = hovered || pinned;
  const close = () => { setHovered(false); setPinned(false); };

  // Web: open on hover (wrapper covers trigger + card so moving between them
  // keeps it open). Native: tap toggles via `pinned`.
  const hoverProps = isWeb
    ? ({ onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) } as any)
    : {};

  // ── The card ──
  // No gap (`marginTop`/`marginBottom`) between trigger and card: a gap of
  // empty space breaks the hover bridge and closes the card mid-move. Use a
  // transparent spacer inside the card instead so the hover area stays
  // continuous while keeping the visual offset.
  const cardPos: ViewStyle = variant === 'navbar'
    ? { position: 'absolute', top: '100%', right: 0, paddingTop: 8 }
    : { position: 'absolute', bottom: '100%', left: 0, paddingBottom: 8 };

  // Full-screen catcher so a click anywhere outside the card closes it.
  const outsideCatcher = pinned ? (
    <TouchableOpacity
      activeOpacity={1}
      onPress={close}
      style={{ position: 'absolute', top: -2000, left: -2000, right: -2000, bottom: -2000, zIndex: 90 } as any}
    />
  ) : null;

  const card = open ? (
    <View
      style={{
        ...cardPos,
        minWidth: Math.min(260, cardMaxWidth),
        maxWidth: cardMaxWidth,
        backgroundColor: colors.card,
        borderWidth: 1,
        borderColor: colors.line,
        borderRadius: radii.r3,
        padding: 6,
        zIndex: 100,
        ...(shadows.lg as any),
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 10, paddingHorizontal: 10 }}>
        <GradientAvatar name={name} size={44} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text numberOfLines={1} style={{ fontSize: fontSize.md, fontWeight: fontWeight.bold, color: colors.ink, fontFamily: theme.font.sans, includeFontPadding: false }}>
            {name}
          </Text>
          {!!email && (
            <Text numberOfLines={1} style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans, includeFontPadding: false, marginTop: 2 }}>
              {email}
            </Text>
          )}
        </View>
      </View>
      <View style={{ height: 1, backgroundColor: colors.line, marginVertical: 4 }} />
      <TouchableOpacity
        onPress={() => { close(); onLogout?.(); }}
        accessibilityLabel="Log out"
        style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, paddingHorizontal: 10, borderRadius: radii.r2, ...(isWeb ? ({ cursor: 'pointer' } as any) : {}) }}
      >
        <Icon name="logout" size={16} color={colors.danger} />
        <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.medium, color: colors.danger, fontFamily: theme.font.sans, includeFontPadding: false }}>
          Log out
        </Text>
      </TouchableOpacity>
    </View>
  ) : null;

  // ── Navbar trigger: rounded pill (name + avatar) ──
  if (variant === 'navbar') {
    const pill: ViewStyle = {
      flexDirection: 'row', alignItems: 'center', gap: 12,
      paddingLeft: 16, paddingRight: 5, paddingVertical: 5,
      borderRadius: 999, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card,
      ...(isWeb ? ({ cursor: 'pointer' } as any) : {}),
    };
    const nameStyle: TextStyle = { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans, includeFontPadding: false };
    return (
      <View style={{ position: 'relative' }} {...hoverProps}>
        {outsideCatcher}
        <TouchableOpacity style={pill} activeOpacity={0.8} onPress={() => setPinned((p) => !p)}>
          {!compact && <Text numberOfLines={1} style={nameStyle}>{name}</Text>}
          <GradientAvatar name={name} size={38} />
        </TouchableOpacity>
        {card}
      </View>
    );
  }

  // ── Sidebar trigger: chip (avatar + name + email), card opens upward ──
  const chip: ViewStyle = {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 8, paddingHorizontal: 8, borderRadius: radii.r2,
    ...(isWeb ? ({ cursor: 'pointer' } as any) : {}),
  };
  return (
    <View style={{ position: 'relative' }} {...hoverProps}>
      {outsideCatcher}
      <TouchableOpacity style={chip} activeOpacity={0.75} onPress={() => setPinned((p) => !p)}>
        <GradientAvatar name={name} size={compact ? 34 : 38} />
        {!compact && (
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text numberOfLines={1} style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans, includeFontPadding: false }}>
              {name}
            </Text>
            {!!email && (
              <Text numberOfLines={1} style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans, includeFontPadding: false }}>
                {email}
              </Text>
            )}
          </View>
        )}
        {!compact && <Icon name="chev-u" size={14} color={colors.ink3} />}
      </TouchableOpacity>
      {card}
    </View>
  );
}
